import React, { useState, useEffect, useRef } from 'react';
import './CoachPlanner.css';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import PlannerTrialStrip from '../swishiq/PlannerTrialStrip';
import { dowLong } from '../utils/dayOfWeek';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// Strip timezone suffix so dates are treated as local time (server stores local AEDT times)
const parseLocalDateTime = (dateStr) => {
  if (!dateStr) return new Date();
  // Remove trailing Z or timezone offset so Date() treats it as local
  const cleaned = String(dateStr).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
  return new Date(cleaned);
};

// Format a DB-stored datetime (Sydney wall-clock, returned by mssql with
// matching UTC components) for human display in tooltips.
const formatLocalStoredDateTime = (value) => {
  if (!value) return '';
  const d = parseLocalDateTime(value);
  if (isNaN(d.getTime())) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}, ${h12}:${m} ${ampm}`;
};

export default function CoachPlanner() {
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('coach-planner', 'coach-planner');

  // Basketball Connect sync (button, and right after a session is created).
  const [bcSyncing, setBcSyncing] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [programs, setPrograms] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [availabilityCache, setAvailabilityCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDayLocked, setIsDayLocked] = useState(false);
  // Modal shown when checking in a NOT-financial participant. Holds the
  // {programId, participant} pair so the buttons know what to act on.
  const [notFinancialPrompt, setNotFinancialPrompt] = useState(null);
  // Set of programIds whose Participants section is currently expanded.
  // Defaults to empty (collapsed) - coaches expand only the tile they're
  // working on so the grid stays compact.
  const [expandedParticipants, setExpandedParticipants] = useState(() => new Set());
  const toggleParticipantsExpanded = (programId) => {
    setExpandedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(programId)) next.delete(programId); else next.add(programId);
      return next;
    });
  };
  const [lockLoading, setLockLoading] = useState(false);

  // Modal state
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programForm, setProgramForm] = useState({
    programTypeId: '', startTime: '', durationMinutes: '60', location: '', ownerId: '', notes: '', isManual: false, repeat: false, repeatFrom: '', repeatUntil: '', repeatSlots: {}
  });
  // Registration Groups of the chosen program (Linked sessions): each is given its own day and time.
  const [regGroups, setRegGroups] = useState([]);
  const [regGroupsLoading, setRegGroupsLoading] = useState(false);
  useEffect(() => {
    if (!showProgramModal || editingProgram || programForm.isManual || !programForm.programTypeId) {
      setRegGroups([]);
      return undefined;
    }
    let cancelled = false;
    setRegGroupsLoading(true);
    auditFetch(`${API_BASE}/coach-planner/reg-groups?programTypeId=${encodeURIComponent(programForm.programTypeId)}`)
      .then(r => (r.ok ? r.json() : { groups: [] }))
      .then(data => {
        if (cancelled) return;
        const groups = Array.isArray(data.groups) ? data.groups : [];
        setRegGroups(groups);
        const slots = {};
        groups.forEach(g => {
          const sg = g.suggested;
          let end = sg && sg.endTime ? sg.endTime : '';
          if (!end && sg && sg.time) {
            const [h, m] = sg.time.split(':').map(Number);
            const tot = h * 60 + m + (sg.durationMinutes || 60);
            end = `${String(Math.floor(tot / 60) % 24).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
          }
          slots[g.divisionId] = {
            on: !!(sg && sg.time),
            dow: sg ? sg.dayOfWeek : '',
            start: sg ? sg.time : '',
            end,
            court: '',
            coachRows: [] // [{ opt: 'coachId_qualificationId', codeId }]: each coach has their own coaching code
          };
        });
        setProgramForm(f => ({ ...f, groupSlots: slots }));
      })
      .catch(() => { if (!cancelled) setRegGroups([]); })
      .finally(() => { if (!cancelled) setRegGroupsLoading(false); });
    return () => { cancelled = true; };
  }, [showProgramModal, editingProgram, programForm.isManual, programForm.programTypeId]);
  const groupMode = !editingProgram && !programForm.isManual && regGroups.length > 0;

  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [participantModalData, setParticipantModalData] = useState({ programId: null, programName: '', participants: [] });
  // (Import Template removed — BC integration now handles bulk session
  // creation; manual weekly templates are deprecated.)
  // (allPeople removed: the session form no longer has an Owner picker.)
  const [coachingCourts, setCoachingCourts] = useState([]);
  const [coachCodes, setCoachCodes] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);

  // Per-session copy (one program, multiple target dates).
  const [copySessionProgram, setCopySessionProgram] = useState(null);
  const [copySessionDates, setCopySessionDates] = useState(['']);
  const [copySessionLoading, setCopySessionLoading] = useState(false);
  const [copySessionResult, setCopySessionResult] = useState(null);

  // Date range: 3 weeks behind and 3 weeks ahead
  // Allow date navigation +/- 365 days from today so a Coaching Program
  // running for a full term/year is fully accessible from the planner.
  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() - 365);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 365);
  const minDateStr = `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}-${String(minDate.getDate()).padStart(2, '0')}`;
  const maxDateStr = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load programs when date changes
  useEffect(() => {
    loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const loadReferenceData = async () => {
    try {
      const [ptRes, coachRes, courtsRes, codesRes] = await Promise.all([
        fetch(`${API_BASE}/program-types`),
        fetch(`${API_BASE}/coaches`),
        fetch(`${API_BASE}/coaching-courts`),
        fetch(`${API_BASE}/coach-codes`)
      ]);
      if (codesRes.ok) setCoachCodes(await codesRes.json());
      if (ptRes.ok) setProgramTypes(await ptRes.json());
      if (courtsRes.ok) setCoachingCourts(await courtsRes.json());
      let coachList = [];
      if (coachRes.ok) {
        coachList = await coachRes.json();
        setCoaches(coachList);
      }

      // Preload availability for all coaches
      const cache = {};
      for (const coach of coachList) {
        try {
          const res = await fetch(`${API_BASE}/person/${coach.id}/availability`);
          if (res.ok) {
            cache[coach.id] = await res.json();
          }
        } catch (err) {
          console.error(`Error loading availability for coach ${coach.id}:`, err);
        }
      }
      setAvailabilityCache(cache);
    } catch (err) {
      console.error('Error loading reference data:', err);
    }
  };

  // Internal — actual fetch. Set `silent=true` to refresh state without
  // flipping the `loading` flag (which would unmount every tile and
  // make the screen jump up while "Loading..." renders, then jump down
  // when the new tiles render). Used by the public reload helpers.
  const fetchProgramsInner = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const [progResponse, lockResponse] = await Promise.all([
        fetch(`${API_BASE}/programs/by-day/${selectedDate}`),
        fetch(`${API_BASE}/locked-days/${selectedDate}?source=coach`)
      ]);
      if (progResponse.ok) {
        setPrograms(await progResponse.json());
      } else {
        setError('Failed to load programs');
      }
      if (lockResponse.ok) {
        const lockData = await lockResponse.json();
        setIsDayLocked(lockData.isLocked);
      }
    } catch (err) {
      setError('Error loading programs: ' + err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial / date-change loads — show the loading placeholder.
  const loadPrograms = () => fetchProgramsInner(false);
  // Post-action refreshes — keep tiles rendered while data updates so
  // the page doesn't shift up + down (and so the per-tile cost doesn't
  // disappear and reappear). All single-action handlers below should use
  // this rather than loadPrograms().
  const silentReloadPrograms = () => fetchProgramsInner(true);

  // Waits for the Basketball Connect sync to finish (checks every 3 seconds, up to about 5 minutes).
  const waitForBcSync = async () => {
    for (let i = 0; i < 100; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (!alive.current) return null;
      try {
        const res = await fetch(`${API_BASE}/coach-planner/sync-status`);
        const status = res.ok ? await res.json() : null;
        if (status && !status.running) return status;
      } catch {
        // try again next time
      }
    }
    return null;
  };

  // A new session starts the sync for its program on the server; this just waits for it and
  // refreshes the day so the players appear.
  const watchBcSyncAfterCreate = async () => {
    setBcSyncing(true);
    try {
      await waitForBcSync();
      if (alive.current) silentReloadPrograms();
    } finally {
      if (alive.current) setBcSyncing(false);
    }
  };

  // "Sync" button: run the Basketball Connect Comp Mapping sync now.
  const handleBcSync = async () => {
    if (!window.confirm('Syncing with Basketball Connect takes 1-2 minutes to complete. Players registered for your coaching programs are added to their sessions.\n\nDo you want to continue?')) return;
    setError('');
    setBcSyncing(true);
    try {
      const res = await auditFetch(`${API_BASE}/coach-planner/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.recent) {
        setSuccess('Basketball Connect was synced a moment ago, so there is nothing new to bring in.');
        setTimeout(() => setSuccess(''), 5000);
        return;
      }
      const status = await waitForBcSync();
      if (!alive.current) return;
      silentReloadPrograms();
      if (!status) {
        setSuccess('The sync is still running. Refresh in a minute.');
      } else if (status.failed > 0) {
        setError(`The sync finished with ${status.failed} mapping${status.failed === 1 ? '' : 's'} failing (${status.ok} ok). Check Admin → BC Registrations.`);
      } else {
        setSuccess('Synced with Basketball Connect.');
      }
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError('Could not sync: ' + err.message);
    } finally {
      if (alive.current) setBcSyncing(false);
    }
  };

  const handleLockDay = async () => {
    try {
      setLockLoading(true);
      const response = await auditFetch(`${API_BASE}/locked-days`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, source: 'coach' })
      });
      if (response.ok) {
        setIsDayLocked(true);
        setSuccess('Day locked successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to lock day');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLockLoading(false);
    }
  };

  // Time off (days off, or a period): the session must not touch it.
  const isCoachOnTimeOff = (coachId, programStartTime, durationMinutes) => {
    const list = availabilityCache[coachId]?.timeOff;
    if (!list || list.length === 0) return false;
    const sessionStartMs = parseLocalDateTime(programStartTime).getTime();
    const sessionEndMs = sessionStartMs + (Number(durationMinutes) || 60) * 60 * 1000;
    return list.some(t => sessionStartMs < parseLocalDateTime(t.endAt).getTime() && sessionEndMs > parseLocalDateTime(t.startAt).getTime());
  };

  // Check if a coach is available for a given program time
  const isCoachAvailable = (coachId, programStartTime, durationMinutes) => {
    try {
      if (!availabilityCache[coachId]) return true; // No data = assume available

      const availability = availabilityCache[coachId];
      const start = parseLocalDateTime(programStartTime);
      if (isCoachOnTimeOff(coachId, programStartTime, durationMinutes)) return false;
      const dayOfWeek = start.getDay();
      const dayAvail = availability.availability?.[dayOfWeek];

      if (!dayAvail) return true; // No restrictions for this day
      if (!dayAvail.isAvailable) return false; // Marked as unavailable for this day

      // Check time window
      const hours = start.getHours();
      const minutes = start.getMinutes();
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + durationMinutes;

      const [startH, startM] = dayAvail.startTime.split(':').map(Number);
      const [endH, endM] = dayAvail.endTime.split(':').map(Number);
      const availStart = startH * 60 + startM;
      const availEnd = endH * 60 + endM;

      return startMinutes >= availStart && endMinutes <= availEnd;
    } catch (err) {
      console.error(`Error checking availability for coach ${coachId}:`, err);
      return true;
    }
  };

  // Check if coach is already assigned to another program at overlapping time
  const isCoachBusy = (coachId, programId, programStartTime, durationMinutes) => {
    const start = parseLocalDateTime(programStartTime).getTime();
    const end = start + durationMinutes * 60000;

    return programs.some(p => {
      if (p.id === programId) return false;
      const pStart = parseLocalDateTime(p.startDateTime).getTime();
      const pEnd = pStart + p.durationMinutes * 60000;
      // Check time overlap
      if (start < pEnd && end > pStart) {
        // Check if coach is assigned to this overlapping program
        return (p.coachAssignments || []).some(a => a.coachId === coachId);
      }
      return false;
    });
  };

  // Get filtered coach options for a program
  const getCoachOptions = (program) => {
    const assignedIds = (program.coachAssignments || []).map(a => a.coachId);
    const available = [];
    const unavailable = [];

    coaches.forEach(coach => {
      if (assignedIds.includes(coach.id)) return; // Already assigned to this program
      const busy = isCoachBusy(coach.id, program.id, program.startDateTime, program.durationMinutes);
      const avail = isCoachAvailable(coach.id, program.startDateTime, program.durationMinutes);
      if (busy) {
        unavailable.push({ ...coach, reason: 'busy' });
      } else if (!avail) {
        unavailable.push({ ...coach, reason: isCoachOnTimeOff(coach.id, program.startDateTime, program.durationMinutes) ? 'time off' : 'unavailable' });
      } else {
        available.push(coach);
      }
    });

    return { available, unavailable };
  };

  // Check if program end time has passed
  const isDayOnOrAfter = (startDateTime) => {
    const programDate = parseLocalDateTime(startDateTime);
    const today = new Date();
    // Compare dates only (ignore time)
    const progDateStr = `${programDate.getFullYear()}-${String(programDate.getMonth() + 1).padStart(2, '0')}-${String(programDate.getDate()).padStart(2, '0')}`;
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return todayStr >= progDateStr;
  };

  // ==================== COACH ASSIGNMENT ====================

  // Dropdown option values are encoded as `${coachId}_${coachQualificationId}`
  // so picking "Jackson Kirkwood Level 4" actually saves Level 4. Falls back
  // gracefully if older callers pass just a bare coachId (no underscore).
  const handleAssignCoach = async (programId, optionValue) => {
    if (!optionValue) return;
    setError('');
    try {
      const [coachIdStr, qualIdStr] = String(optionValue).split('_');
      const coachId = parseInt(coachIdStr, 10);
      let coachQualificationId = qualIdStr ? parseInt(qualIdStr, 10) : null;
      if (!coachQualificationId) {
        // Legacy fallback: option value didn't include qualification — use the
        // coach's first qualification record (matches old behaviour).
        const coach = coaches.find(c => c.id === coachId);
        coachQualificationId = coach?.coachQualificationId || null;
      }
      const response = await auditFetch(`${API_BASE}/programs/${programId}/assign-coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coachId, coachQualificationId })
      });
      if (response.ok) {
        silentReloadPrograms();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to assign coach');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // Coach code chip (Admin → Coach Codes): each click moves the coach to the
  // next code. Updates the tile in place.
  const handleCycleCoachCode = async (programId, assignmentId) => {
    setError('');
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/coaches/${assignmentId}/cycle-code`, { method: 'PUT' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) { setError(data.error || 'Failed to change coach code'); return; }
      setPrograms(prev => prev.map(p => (p.id !== programId ? p : {
        ...p,
        coachAssignments: (p.coachAssignments || []).map(a => (a.id !== assignmentId ? a
          : { ...a, coachCodeId: data.coachCodeId, coachCode: data.code, coachCodeLabel: data.label }))
      })));
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleRemoveCoach = async (programId, assignmentId) => {
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/coaches/${assignmentId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        silentReloadPrograms();
      } else {
        setError('Failed to remove coach');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleAttendanceToggle = async (programId, assignmentId, currentStatus) => {
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/coaches/${assignmentId}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attended: !currentStatus })
      });
      if (response.ok) {
        silentReloadPrograms();
      } else {
        setError('Failed to update attendance');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // Send a signup-reminder SMS for a non-financial participant.
  // Surfaced from the financial-warning prompt during attendance check-in.
  const sendSignupReminderSms = async (participantId, programTypeId, displayName) => {
    try {
      const res = await auditFetch(`${API_BASE}/sms/signup-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participantId, programTypeId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        const msg = data.error || (data.results?.[0]?.error) || 'unknown error';
        window.alert(`Couldn't send signup reminder to ${displayName}: ${msg}`);
        return false;
      }
      window.alert(`Signup reminder SMS sent to ${displayName}.`);
      return true;
    } catch (err) {
      window.alert(`Couldn't send signup reminder: ${err.message}`);
      return false;
    }
  };

  // Optimistic local update so the page doesn't refresh + scroll-jump on
  // every attendance tick. Roll back if the server rejects.
  const flipAttendanceLocally = (programId, participantId, attended) => {
    setPrograms(prev => prev.map(p => {
      if (p.id !== programId) return p;
      return {
        ...p,
        selectedParticipants: (p.selectedParticipants || []).map(part =>
          part.participantId === participantId ? { ...part, attended } : part
        )
      };
    }));
  };

  const writeAttendance = async (programId, participantId, attended) => {
    // Apply locally up-front so the checkbox responds instantly.
    flipAttendanceLocally(programId, participantId, attended);
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/participants/${participantId}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attended })
      });
      if (!response.ok) {
        flipAttendanceLocally(programId, participantId, !attended); // revert
        // Surface the backend's actual error so locked-day rejections, missing
        // PPA rows, etc. don't all read as the same opaque message.
        const data = await response.json().catch(() => ({}));
        setError(data.error || `Failed to update participant attendance (HTTP ${response.status})`);
      }
    } catch (err) {
      flipAttendanceLocally(programId, participantId, !attended); // revert
      setError('Error: ' + err.message);
    }
  };

  const handleParticipantAttendanceToggle = async (programId, participant, currentStatus) => {
    const participantId = participant.participantId;
    const newStatus = !currentStatus;
    // When marking a NOT-financial participant as ATTENDED, open a modal with
    // three real buttons (Checkin and Notify / Checkin / Cancel) instead of
    // a text-only prompt.
    if (newStatus && participant && participant.isFinancial === false) {
      setNotFinancialPrompt({ programId, participant });
      return; // modal will continue the flow
    }
    await writeAttendance(programId, participantId, newStatus);
  };

  // Modal button handlers ----------------------------------------------------
  const handleNotFinancialCheckin = async ({ notify }) => {
    if (!notFinancialPrompt) return;
    const { programId, participant } = notFinancialPrompt;
    const participantId = participant.participantId;
    const displayName = `${participant.firstName} ${participant.lastName}`;
    setNotFinancialPrompt(null);
    if (notify) {
      if (participant.programSignupUrl) {
        await sendSignupReminderSms(participantId, participant.programTypeId, displayName);
      } else {
        window.alert(`Can't send reminder: ${participant.programTypeName || 'this program'} has no signup URL set in Admin → Coaching Programs.`);
      }
    }
    await writeAttendance(programId, participantId, true);
  };

  // Bulk-toggle attendance for every participant on a tile. Used by the
  // Select All / Deselect All link on each program tile. Skips the per-row
  // financial prompt — coaches can still flip individuals to trigger SMS
  // reminders one-by-one.
  const handleBulkAttendance = async (programId, attended) => {
    // Optimistic local update so the page doesn't refresh + scroll-jump.
    // Capture the previous state in case we need to revert.
    let previousState;
    setPrograms(prev => {
      previousState = prev;
      return prev.map(p => {
        if (p.id !== programId) return p;
        return {
          ...p,
          selectedParticipants: (p.selectedParticipants || []).map(part => ({ ...part, attended }))
        };
      });
    });
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/participants/attendance/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attended })
      });
      if (!response.ok) {
        if (previousState) setPrograms(previousState); // revert
        const data = await response.json().catch(() => ({}));
        setError(data.error || 'Failed to update attendance');
      }
    } catch (err) {
      if (previousState) setPrograms(previousState); // revert
      setError('Error: ' + err.message);
    }
  };

  // ==================== PROGRAM CRUD ====================

  const handleSaveProgram = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (groupMode) {
        const toMin = (t) => { const [h, m] = String(t || '').split(':').map(Number); return h * 60 + m; };
        const chosen = regGroups.filter(g => programForm.groupSlots?.[g.divisionId]?.on);
        if (chosen.length === 0) { setError('Tick at least one registration group.'); return; }
        const groupsBody = [];
        for (const g of chosen) {
          const sl = programForm.groupSlots[g.divisionId];
          const dur = toMin(sl.end) - toMin(sl.start);
          if (sl.dow === '' || !sl.start || !sl.end || !(dur > 0)) {
            setError(`Set a day, a start time and a later end time for "${g.divisionName}".`);
            return;
          }
          groupsBody.push({
            divisionId: g.divisionId,
            dayOfWeek: Number(sl.dow),
            time: sl.start,
            durationMinutes: dur,
            location: sl.court || null,
            coaches: (sl.coachRows || []).map((row) => {
              const [cid, qid] = String(row.opt).split('_');
              return { coachId: Number(cid), coachQualificationId: qid ? Number(qid) : null, coachCodeId: row.codeId ? Number(row.codeId) : null };
            })
          });
        }
        if (!programForm.repeatFrom) { setError('Choose a start date.'); return; }
        let endDate = programForm.repeatFrom;
        if (programForm.repeat) {
          if (!programForm.repeatUntil) { setError('Choose an end date for the repeat.'); return; }
          if (programForm.repeatUntil < programForm.repeatFrom) { setError('The end date is before the start date.'); return; }
          endDate = programForm.repeatUntil;
        } else {
          // One session per group: the first matching day within a week of the start date.
          const d = new Date(`${programForm.repeatFrom}T00:00:00Z`);
          d.setUTCDate(d.getUTCDate() + 6);
          endDate = d.toISOString().slice(0, 10);
        }
        const resp = await auditFetch(`${API_BASE}/programs/reg-group-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            programTypeId: parseInt(programForm.programTypeId),
            startDate: programForm.repeatFrom,
            endDate,
            location: programForm.location,
            notes: programForm.notes || null,
            groups: groupsBody
          })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) { setError(data.error || 'Failed to create the sessions'); return; }
        setSuccess(`${data.created} session${data.created === 1 ? '' : 's'} added with ${data.attached} player${data.attached === 1 ? '' : 's'}${data.skipped ? ` (${data.skipped} already existed)` : ''}. Basketball Connect is being checked for anyone newer.`);
        setTimeout(() => setSuccess(''), 8000);
        setShowProgramModal(false);
        setEditingProgram(null);
        setProgramForm({ programTypeId: '', startTime: '', durationMinutes: '60', location: '', ownerId: '', notes: '', isManual: false, repeat: false, repeatFrom: '', repeatUntil: '', repeatSlots: {}, groupSlots: {} });
        silentReloadPrograms();
        if (data.created > 0) watchBcSyncAfterCreate();
        return;
      }
      if (!editingProgram && programForm.repeat) {
        const slots = Object.entries(programForm.repeatSlots || {})
          .filter(([, t]) => t)
          .map(([dow, time]) => ({ dayOfWeek: Number(dow), time }));
        if (!programForm.repeatFrom || !programForm.repeatUntil) { setError('Choose a start date and an end date for the repeat.'); return; }
        if (programForm.repeatUntil < programForm.repeatFrom) { setError('The end date is before the start date.'); return; }
        if (slots.length === 0) { setError('Tick at least one day and set its start time.'); return; }
        const resp = await auditFetch(`${API_BASE}/programs/recurring`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            programTypeId: parseInt(programForm.programTypeId),
            startDate: programForm.repeatFrom,
            endDate: programForm.repeatUntil,
            slots,
            durationMinutes: parseInt(programForm.durationMinutes),
            location: programForm.location,
            notes: programForm.notes || null,
            isManual: !!programForm.isManual
          })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) { setError(data.error || 'Failed to create the repeating sessions'); return; }
        const manual = !!programForm.isManual;
        setSuccess(`${data.created} session${data.created === 1 ? '' : 's'} added${data.skipped ? ` (${data.skipped} already existed)` : ''}.${manual ? ' Add the players yourself.' : ''}`);
        setTimeout(() => setSuccess(''), 6000);
        setShowProgramModal(false);
        setEditingProgram(null);
        setProgramForm({ programTypeId: '', startTime: '', durationMinutes: '60', location: '', ownerId: '', notes: '', isManual: false, repeat: false, repeatFrom: '', repeatUntil: '', repeatSlots: {}, groupSlots: {} });
        silentReloadPrograms();
        if (!manual && data.created > 0) watchBcSyncAfterCreate();
        return;
      }
      const startDateTime = `${selectedDate}T${programForm.startTime}:00`;
      const body = {
        programTypeId: parseInt(programForm.programTypeId),
        startDateTime,
        durationMinutes: parseInt(programForm.durationMinutes),
        location: programForm.location,
        ownerId: programForm.ownerId ? parseInt(programForm.ownerId) : null,
        notes: programForm.notes || null
      };
      if (!editingProgram) body.isManual = !!programForm.isManual;

      const url = editingProgram ? `${API_BASE}/programs/${editingProgram.id}` : `${API_BASE}/programs`;
      const method = editingProgram ? 'PUT' : 'POST';

      const response = await auditFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const wasNew = !editingProgram;
        const wasManual = wasNew && !!programForm.isManual;
        setSuccess(editingProgram ? 'Program updated!' : wasManual ? 'Manual session added. Add the players yourself.' : 'Program added!');
        setTimeout(() => setSuccess(''), 3000);
        setShowProgramModal(false);
        setEditingProgram(null);
        setProgramForm({ programTypeId: '', startTime: '', durationMinutes: '60', location: '', ownerId: '', notes: '', isManual: false, repeat: false, repeatFrom: '', repeatUntil: '', repeatSlots: {}, groupSlots: {} });
        silentReloadPrograms();
        // The server also starts a Basketball Connect sync for the new session's program.
        if (wasNew && !wasManual) watchBcSyncAfterCreate();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save program');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditProgram = (program) => {
    const startTime = parseLocalDateTime(program.startDateTime);
    const hours = String(startTime.getHours()).padStart(2, '0');
    const minutes = String(startTime.getMinutes()).padStart(2, '0');
    setProgramForm({
      programTypeId: String(program.programTypeId),
      startTime: `${hours}:${minutes}`,
      durationMinutes: String(program.durationMinutes),
      location: program.location || '',
      ownerId: program.ownerId ? String(program.ownerId) : '',
      notes: program.notes || ''
    });
    setEditingProgram(program);
    setShowProgramModal(true);
  };

  const handleArchiveProgram = async (programId) => {
    if (!window.confirm('Archive this program? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Program archived!');
        setTimeout(() => setSuccess(''), 3000);
        silentReloadPrograms();
      } else {
        setError('Failed to archive program');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== IMPORT TEMPLATE ====================

  // (handleImportTemplate removed — see comment near state block.)

  // ==================== PARTICIPANTS ====================

  const handleViewParticipants = async (program) => {
    try {
      const response = await fetch(`${API_BASE}/programs/${program.id}/participants`);
      if (response.ok) {
        const data = await response.json();
        setParticipantModalData({
          programId: program.id,
          programName: program.programTypeName || 'Program',
          participants: data
        });
        setShowParticipantModal(true);
      } else {
        setError('Failed to load participants');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleToggleParticipant = async (programId, participantId) => {
    try {
      const response = await auditFetch(`${API_BASE}/programs/${programId}/participants/${participantId}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        // Refresh participant list in modal
        const refreshRes = await fetch(`${API_BASE}/programs/${programId}/participants`);
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          setParticipantModalData(prev => ({ ...prev, participants: data }));
        }
        // Refresh programs to update tile display
        silentReloadPrograms();
      } else {
        setError('Failed to update participant');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== COPY DAY ====================
  // (UI button removed 16 May 2026 at operator request — handler kept
  // available so the modal still works if some future flow opens it.)

  const handleCopyDay = async () => {
    if (!copyTargetDate) {
      setError('Please select a target date');
      return;
    }
    setCopyLoading(true);
    setError('');
    try {
      const response = await auditFetch(`${API_BASE}/programs/copy-day`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceDate: selectedDate, targetDate: copyTargetDate })
      });
      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || `Copied programs to ${copyTargetDate}!`);
        setTimeout(() => setSuccess(''), 3000);
        setShowCopyModal(false);
        setCopyTargetDate('');
        // If the target date is the currently viewed date, reload
        if (copyTargetDate === selectedDate) silentReloadPrograms();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to copy day');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setCopyLoading(false);
    }
  };

  // ==================== COPY SESSION (single program → multiple dates) ====================

  const openCopySession = (program) => {
    setCopySessionProgram(program);
    setCopySessionDates(['']);
    setCopySessionResult(null);
    setError('');
  };
  const closeCopySession = () => {
    setCopySessionProgram(null);
    setCopySessionDates(['']);
    setCopySessionResult(null);
    setCopySessionLoading(false);
  };
  const addCopySessionDate = () => setCopySessionDates(prev => [...prev, '']);
  const removeCopySessionDate = (i) => setCopySessionDates(prev => prev.filter((_, idx) => idx !== i).length === 0 ? [''] : prev.filter((_, idx) => idx !== i));
  const setCopySessionDate = (i, v) => setCopySessionDates(prev => prev.map((d, idx) => idx === i ? v : d));

  const handleCopySession = async () => {
    if (!copySessionProgram) return;
    const dates = copySessionDates.map(d => (d || '').trim()).filter(Boolean);
    if (dates.length === 0) { setError('Pick at least one target date.'); return; }
    setCopySessionLoading(true); setError(''); setCopySessionResult(null);
    try {
      const res = await auditFetch(`${API_BASE}/programs/${copySessionProgram.id}/copy-to-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetDates: dates })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed (HTTP ${res.status})`);
        return;
      }
      setCopySessionResult(data);
      // Refresh the currently-viewed day's data if it changed.
      const currentDate = selectedDate;
      if (data.created.some(c => c.date === currentDate)) silentReloadPrograms();
      setSuccess(`Copied to ${data.created.length} date${data.created.length === 1 ? '' : 's'}${data.skipped.length ? ` (${data.skipped.length} skipped)` : ''}.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setCopySessionLoading(false);
    }
  };

  // ==================== GRID BUILDING ====================

  // Build columns by court (location) across the top, time slots as rows
  const buildGrid = () => {
    if (programs.length === 0) return { timeSlots: [], columns: [] };

    // Build columns from courts that have programs
    const courtOrder = coachingCourts.map(c => c.name);
    const usedCourts = new Set(programs.map(p => p.location || ''));

    // Sort: known courts in order first, then any unknown locations
    const sortedCourts = [
      ...courtOrder.filter(c => usedCourts.has(c)),
      ...[...usedCourts].filter(c => c && !courtOrder.includes(c)).sort()
    ];
    // Add empty location if any programs have no court
    if (usedCourts.has('')) sortedCourts.push('');

    const columns = sortedCourts.map(court => ({
      key: court || '_none_',
      location: court,
      headerLabel: court || 'No Court'
    }));

    // Group programs by time slot, then by court
    const timeMap = {};
    programs.forEach(p => {
      const start = parseLocalDateTime(p.startDateTime);
      const timeKey = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
      const colKey = p.location || '_none_';
      if (!timeMap[timeKey]) timeMap[timeKey] = {};
      if (!timeMap[timeKey][colKey]) timeMap[timeKey][colKey] = [];
      timeMap[timeKey][colKey].push(p);
    });

    const timeSlots = Object.keys(timeMap).sort().map(time => ({
      time,
      programs: timeMap[time]
    }));

    return { timeSlots, columns };
  };

  const { timeSlots, columns } = buildGrid();
  const gridTemplateColumns = `80px ${columns.map(() => 'minmax(0, 1fr)').join(' ')}`;

  // Format time for display
  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${m} ${ampm}`;
  };

  return (
    <div className="coach-planner">
      {/* NOT-financial check-in confirmation modal */}
      {notFinancialPrompt && (() => {
        const { participant } = notFinancialPrompt;
        const displayName = `${participant.firstName} ${participant.lastName}`;
        const programName = participant.programTypeName || 'this program';
        const noSignupUrl = !participant.programSignupUrl;
        return (
          <div
            role="dialog"
            aria-modal="true"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
            onClick={() => setNotFinancialPrompt(null)}
          >
            <div
              style={{ background: 'white', borderRadius: 10, padding: '1.5rem 1.75rem', maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 0.75rem 0', color: '#c62828', fontSize: '1.05rem' }}>Not financial</h3>
              <p style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '0.95rem', lineHeight: 1.5 }}>
                <strong>{displayName}</strong> is NOT financial for <strong>{programName}</strong>.
              </p>
              <ul style={{ margin: '0 0 1rem 1.1rem', padding: 0, color: '#444', fontSize: '0.9rem', lineHeight: 1.5 }}>
                <li>Select <strong>Checkin and Notify</strong> to mark as attended AND send a signup reminder SMS.</li>
                <li>Select <strong>Checkin</strong> to mark as attended only.</li>
                <li>Select <strong>Cancel</strong> to abort.</li>
              </ul>
              {noSignupUrl && (
                <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderLeft: '3px solid #f9a825', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8rem', color: '#7f5e00' }}>
                  No signup URL is set for this Coaching Program — Checkin and Notify will fail. Set one in Admin → Coaching Programs.
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setNotFinancialPrompt(null)}
                  style={{ padding: '0.5rem 1rem', background: '#fff', border: '1px solid #999', color: '#555', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleNotFinancialCheckin({ notify: false })}
                  style={{ padding: '0.5rem 1rem', background: '#1565c0', border: '1px solid #0d47a1', color: 'white', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                >
                  Checkin
                </button>
                <button
                  onClick={() => handleNotFinancialCheckin({ notify: true })}
                  disabled={noSignupUrl}
                  title={noSignupUrl ? 'No signup URL set for this Coaching Program' : 'Mark attended AND send signup-reminder SMS'}
                  style={{
                    padding: '0.5rem 1rem',
                    background: noSignupUrl ? '#aaa' : '#2e7d32',
                    border: '1px solid ' + (noSignupUrl ? '#888' : '#1b5e20'),
                    color: 'white',
                    borderRadius: 4,
                    cursor: noSignupUrl ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Checkin and Notify
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="coach-planning-controls">
        <div className="date-selector">
          <button
            className="btn-day-nav"
            onClick={() => {
              const [y, m, d] = selectedDate.split('-').map(Number);
              const date = new Date(y, m - 1, d - 1);
              const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              if (newDate >= minDateStr) setSelectedDate(newDate);
            }}
            disabled={selectedDate <= minDateStr}
          >&larr;</button>
          <label>Date:</label>
          <input
            type="date"
            value={selectedDate}
            min={minDateStr}
            max={maxDateStr}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <span style={{ fontWeight: 600, color: '#1565c0', fontSize: '0.9rem' }} title="Day of week for the picked date">
            {dowLong(selectedDate)}
          </span>
          <button
            className="btn-day-nav"
            onClick={() => {
              const [y, m, d] = selectedDate.split('-').map(Number);
              const date = new Date(y, m - 1, d + 1);
              const newDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              if (newDate <= maxDateStr) setSelectedDate(newDate);
            }}
            disabled={selectedDate >= maxDateStr}
          >&rarr;</button>
        </div>
        {!readOnly && !isDayLocked && (
          <>
            {/* Copy Day button removed at operator request 16 May 2026.
                The handler, modal, and backend endpoint stay in place so
                an admin or future UI can still trigger a copy if needed —
                we just don't expose it on Coach Planner anymore because
                it was rarely used and easy to misclick. */}
            <button className="btn-add-program" onClick={() => {
              setEditingProgram(null);
              setProgramForm({ programTypeId: '', startTime: '', durationMinutes: '60', location: '', ownerId: '', notes: '', isManual: false, repeat: false, repeatFrom: selectedDate, repeatUntil: '', repeatSlots: {}, groupSlots: {} });
              setShowProgramModal(true);
            }}>
              + Add Session
            </button>
          </>
        )}
        <button
          type="button"
          className="btn-lock"
          onClick={() => { window.location.hash = '#people/participants'; }}
          title="Open the Participants list (People > Participants)"
        >
          Participants
        </button>
        {!readOnly && (
          <button
            className="btn-lock"
            onClick={handleBcSync}
            disabled={bcSyncing}
            title="Bring in new Basketball Connect registrations for your coaching programs"
          >
            {bcSyncing ? 'Syncing…' : '⟳ Sync'}
          </button>
        )}
        {!readOnly && (
          <button
            className={isDayLocked ? 'btn-locked' : 'btn-lock'}
            onClick={handleLockDay}
            disabled={isDayLocked || lockLoading}
          >
            {isDayLocked ? 'Day Locked' : lockLoading ? 'Locking...' : 'Lock Day'}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {bcSyncing && <div className="success-message">Syncing with Basketball Connect: players are added to your sessions as it finishes (1-2 minutes).</div>}
      {loading && <div className="loading">Loading programs...</div>}

      <PlannerTrialStrip date={selectedDate} />

      {!loading && programs.length === 0 && (
        <div className="no-programs-message">
          No sessions scheduled for this date. Use "+ Add Session" to create one manually, or set up the BC integration mapping under Admin → BC Integration to bulk-create sessions for a competition.
        </div>
      )}

      {!loading && programs.length > 0 && (
        <div className="coach-schedule-grid">
          {/* Header */}
          <div className="coach-schedule-header" style={{ gridTemplateColumns }}>
            <div className="coach-time-column-header">Time</div>
            {columns.map(col => (
              <div key={col.key} className="coach-type-header">{col.headerLabel}</div>
            ))}
          </div>

          {/* Rows */}
          {timeSlots.map(slot => (
            <div key={slot.time} className="coach-schedule-row" style={{ gridTemplateColumns }}>
              <div className="coach-time-slot">{formatTime(slot.time)}</div>
              {columns.map(col => {
                const cellPrograms = slot.programs[col.key];
                if (!cellPrograms || cellPrograms.length === 0) {
                  return <div key={col.key} className="coach-program-cell"><div className="coach-empty-cell" /></div>;
                }

                return (
                  <div key={col.key} className="coach-program-cell">
                    {cellPrograms.map(program => {
                      const assignments = program.coachAssignments || [];
                      const participants = program.selectedParticipants || [];
                      const { available, unavailable } = getCoachOptions(program);
                      const timePassed = isDayOnOrAfter(program.startDateTime);
                      const totalCost = assignments.reduce((sum, a) => {
                        if (a.response === 'rejected') return sum;
                        const hours = (program.durationMinutes || 0) / 60;
                        return sum + (hours * (a.payPerHour || 0));
                      }, 0);

                      return (
                        <div key={program.id} className="coach-program-tile">
                          <div className="program-tile-header">
                            <div className="program-tile-title" title={program.programTypeName}>
                              {program.programTypeName}
                              {!!program.isManual && (
                                <span title="Manual (unlinked): players are added by hand, never from Basketball Connect"
                                  style={{ marginLeft: 6, fontSize: '0.7em', fontWeight: 600, padding: '1px 6px', borderRadius: 8, border: '1px solid currentColor', opacity: 0.75, whiteSpace: 'nowrap' }}>
                                  Manual
                                </span>
                              )}
                              {program.notes && (
                                <button
                                  className="tile-notes-tag"
                                  onClick={() => handleEditProgram(program)}
                                  title="View/edit notes"
                                >
                                  Notes
                                </button>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              {totalCost > 0 && (
                                <span className="tile-cost">${totalCost.toFixed(2)}</span>
                              )}
                              {!readOnly && !isDayLocked && (
                                <div className="program-tile-actions">
                                  <button onClick={() => openCopySession(program)} title="Copy to other date(s)">&#10697;</button>
                                  <button onClick={() => handleEditProgram(program)} title="Edit">&#9998;</button>
                                  <button onClick={() => handleArchiveProgram(program.id)} title="Archive">&#10005;</button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="program-tile-info">
                            {program.durationMinutes} min
                            {program.ownerName && ` | ${program.ownerName}`}
                          </div>

                          {/* Coach Assignments */}
                          <div className="coach-selectors">
                            {assignments.map(a => (
                              <div key={a.id} className="coach-selector-row">
                                {(() => {
                                  const chipStyle = {
                                    flexShrink: 0, minWidth: 24, padding: '1px 5px', fontSize: '0.65rem', fontWeight: 700,
                                    color: '#37474f', background: '#eceff1', border: '1px solid #b0bec5', borderRadius: 3,
                                    textAlign: 'center', lineHeight: '1.4'
                                  };
                                  const text = a.coachCode || '—';
                                  const label = a.coachCodeLabel || 'No code';
                                  return readOnly || isDayLocked
                                    ? <span style={chipStyle} title={label}>{text}</span>
                                    : (
                                      <button type="button" style={{ ...chipStyle, cursor: 'pointer' }} title={`${label} — click to change`}
                                        onClick={() => handleCycleCoachCode(program.id, a.id)}>{text}</button>
                                    );
                                })()}
                                {(() => {
                                  // Encoded value matches the available/unavailable option format
                                  // so the select correctly highlights the current selection.
                                  const aValue = `${a.coachId}_${a.coachQualificationId || ''}`;
                                  return a.response === 'rejected' && !readOnly && !isDayLocked ? (
                                    <select
                                      value={aValue}
                                      style={{ flex: 1, minWidth: 0, maxWidth: '100%', padding: '0.2rem', fontSize: '0.7rem', border: '1px solid #ccc', borderRadius: '3px', background: '#ffcdd2' }}
                                      onChange={async (e) => {
                                        if (!e.target.value) return;
                                        await handleRemoveCoach(program.id, a.id);
                                        await handleAssignCoach(program.id, e.target.value);
                                      }}
                                    >
                                      <option value={aValue} style={{ color: '#c62828' }}>{a.coachName}{a.qualificationLevel ? ` - ${a.qualificationLevel}` : ''} (Rejected)</option>
                                      {available.map(c => (
                                        <option key={`${c.id}_${c.coachQualificationId || 'x'}`} value={`${c.id}_${c.coachQualificationId || ''}`}>{c.name}{c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''}</option>
                                      ))}
                                      {unavailable.length > 0 && <option disabled>---Unavailable---</option>}
                                      {unavailable.map(c => (
                                        <option key={`${c.id}_${c.coachQualificationId || 'x'}`} value={`${c.id}_${c.coachQualificationId || ''}`} style={{ color: '#999' }}>{c.name}{c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''} ({c.reason})</option>
                                      ))}
                                    </select>
                                  ) : (() => {
                                    // Pill colour by response state:
                                    //   accepted          -> green
                                    //   seen, no response -> orange (viewed in My Portal but didn't accept/reject)
                                    //   else (unseen, no response) -> grey
                                    let pillBg = '#f0f0f0';
                                    let pillTitle;
                                    if (a.response === 'accepted') {
                                      pillBg = '#a5d6a7';
                                      pillTitle = 'Accepted in My Portal';
                                    } else if (a.seenAt) {
                                      pillBg = '#ffe0b2'; // light orange
                                      const seenWhen = formatLocalStoredDateTime(a.seenAt);
                                      pillTitle = `Seen in My Portal${seenWhen ? ' — ' + seenWhen : ''}; no response yet`;
                                    } else {
                                      pillTitle = 'Not yet viewed in My Portal';
                                    }
                                    return (
                                      <select
                                        value={aValue}
                                        disabled
                                        title={pillTitle}
                                        style={{ flex: 1, minWidth: 0, maxWidth: '100%', padding: '0.2rem', fontSize: '0.7rem', border: '1px solid #ccc', borderRadius: '3px', background: pillBg }}
                                      >
                                        <option value={aValue}>{a.coachName}{a.qualificationLevel ? ` - ${a.qualificationLevel}` : ''}</option>
                                      </select>
                                    );
                                  })();
                                })()}
                                <input
                                  type="checkbox"
                                  className="coach-attendance-tick"
                                  checked={a.attended || false}
                                  disabled={!timePassed || readOnly || isDayLocked}
                                  onChange={() => handleAttendanceToggle(program.id, a.id, a.attended)}
                                  title={isDayLocked ? 'Day is locked' : timePassed ? 'Mark attendance' : 'Available on or after program day'}
                                />
                                {!readOnly && !isDayLocked && (
                                  <button
                                    className="coach-remove-btn"
                                    onClick={() => handleRemoveCoach(program.id, a.id)}
                                    title="Remove coach"
                                  >
                                    &#10005;
                                  </button>
                                )}
                              </div>
                            ))}

                            {/* Add new coach dropdown */}
                            {!readOnly && !isDayLocked && assignments.length < 20 && (
                              <div className="coach-selector-row">
                                <select
                                  value=""
                                  onChange={(e) => handleAssignCoach(program.id, e.target.value)}
                                  style={{ flex: 1, minWidth: 0, maxWidth: '100%', padding: '0.2rem', fontSize: '0.7rem', border: '1px solid #ccc', borderRadius: '3px' }}
                                >
                                  <option value="">+ Assign Coach</option>
                                  {available.length > 0 && (
                                    <optgroup label="Available">
                                      {available.map(c => (
                                        <option key={`${c.id}_${c.coachQualificationId || 'x'}`} value={`${c.id}_${c.coachQualificationId || ''}`}>{c.name}{c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {unavailable.length > 0 && (
                                    <optgroup label="Unavailable">
                                      {unavailable.map(c => (
                                        <option key={`${c.id}_${c.coachQualificationId || 'x'}`} value={`${c.id}_${c.coachQualificationId || ''}`} style={{ color: '#999' }}>
                                          {c.name}{c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''} ({c.reason})
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                </select>
                              </div>
                            )}
                          </div>

                          {/* Selected Participants displayed on tile.
                              Hide BC participants whose Registration Group
                              doesn't match THIS session — they're returned by
                              the endpoint (so the "Select Other Participants"
                              modal can still pull them in) but the inline
                              tile list only shows people who actually belong
                              here. inRegistrationGroup defaults to true for
                              manual sessions / non-BC participants. */}
                          {(() => {
                            const visibleParticipants = participants.filter(p => p.inRegistrationGroup !== false);
                            if (visibleParticipants.length === 0) return null;
                            const allAttended = visibleParticipants.every(p => !!p.attended);
                            const bulkDisabled = !timePassed || readOnly || isDayLocked;
                            const isExpanded = expandedParticipants.has(program.id);
                            return (
                              <div className="tile-participants">
                                <div className="tile-participants-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                  <button
                                    type="button"
                                    className="btn-toggle-participants"
                                    onClick={() => toggleParticipantsExpanded(program.id)}
                                    title={isExpanded ? 'Hide participants' : 'Show participants'}
                                  >
                                    <span aria-hidden="true" style={{ fontSize: '0.85rem', display: 'inline-block', transition: 'transform 0.15s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9656;</span>
                                    <span>Participants ({visibleParticipants.filter(p => p.attended).length}/{visibleParticipants.length})</span>
                                  </button>
                                  {isExpanded && (
                                  <button
                                    type="button"
                                    onClick={() => handleBulkAttendance(program.id, !allAttended)}
                                    disabled={bulkDisabled}
                                    title={
                                      isDayLocked ? 'Day is locked'
                                      : !timePassed ? 'Available on or after program day'
                                      : allAttended ? 'Mark every participant as not attended'
                                      : 'Mark every participant as attended'
                                    }
                                    style={{
                                      fontSize: '0.7rem',
                                      padding: '2px 8px',
                                      border: '1px solid ' + (bulkDisabled ? '#ccc' : '#1565c0'),
                                      background: bulkDisabled ? '#f5f5f5' : 'white',
                                      color: bulkDisabled ? '#999' : '#1565c0',
                                      borderRadius: 3,
                                      cursor: bulkDisabled ? 'not-allowed' : 'pointer',
                                      fontWeight: 600
                                    }}
                                  >
                                    {allAttended ? 'Deselect All' : 'Select All'}
                                  </button>
                                  )}
                                </div>
                                {isExpanded && (
                                <div className="tile-participants-list">
                                  {visibleParticipants.map((p, idx) => {
                                    const notFinancial = p.isFinancial === false;
                                    const financial = p.isFinancial === true;
                                    const financialUnknown = p.isFinancial == null;
                                    // inRegistrationGroup === false means this
                                    // participant was added via "Select Other
                                    // Participants" (they're in a different
                                    // BC Reg Group). We tint their attended
                                    // badge a medium green so it's distinct
                                    // from regular-roster attendees but still
                                    // clearly "selected and present".
                                    const offRoster = p.inRegistrationGroup === false;
                                    // Colour scheme by financial status:
                                    //   Financial    -> light green (default), dark green when attended
                                    //                   medium green when attended AND off-roster
                                    //   Not financial-> light red (default),   dark red when attended
                                    //   Unknown      -> light grey (default),  dark grey when attended
                                    let badgeStyle;
                                    if (financial) {
                                      badgeStyle = p.attended
                                        ? (offRoster
                                            ? { background: '#66bb6a', borderColor: '#43a047', color: '#fff' }
                                            : { background: '#2e7d32', borderColor: '#1b5e20', color: '#fff' })
                                        : { background: '#e8f5e9', borderColor: '#a5d6a7', color: '#2e7d32' };
                                    } else if (notFinancial) {
                                      badgeStyle = p.attended
                                        ? { background: '#c62828', borderColor: '#8a1117', color: '#fff' }
                                        : { background: '#ffebee', borderColor: '#ef9a9a', color: '#c62828' };
                                    } else {
                                      badgeStyle = p.attended
                                        ? (offRoster
                                            ? { background: '#9e9e9e', borderColor: '#757575', color: '#fff' }
                                            : { background: '#616161', borderColor: '#424242', color: '#fff' })
                                        : { background: '#f5f5f5', borderColor: '#bdbdbd', color: '#555' };
                                    }
                                    badgeStyle = { ...badgeStyle, borderWidth: 1, borderStyle: 'solid' };
                                    const badgeTitle = isDayLocked
                                      ? 'Day is locked'
                                      : timePassed
                                        ? (notFinancial ? 'Not financial — checking attendance will prompt to send a signup reminder' : 'Mark attendance')
                                        : 'Available on or after program day';
                                    const tapDisabled = !timePassed || readOnly || isDayLocked;
                                    return (
                                      // Whole chip is a label so the entire
                                      // surface is a tap target, not just the
                                      // tiny checkbox in the corner.
                                      <label
                                        key={p.participantId || idx}
                                        className={`tile-participant-badge${p.attended ? ' attended' : ''}`}
                                        style={{ ...badgeStyle, cursor: tapDisabled ? 'not-allowed' : 'pointer' }}
                                        title={notFinancial ? `${p.firstName} ${p.lastName} is NOT financial for this program.` : badgeTitle}
                                      >
                                        <input
                                          type="checkbox"
                                          className="participant-attendance-tick"
                                          checked={p.attended || false}
                                          disabled={tapDisabled}
                                          onChange={() => handleParticipantAttendanceToggle(program.id, p, p.attended)}
                                        />
                                        {p.firstName} {p.lastName}
                                        {notFinancial && <span style={{ marginLeft: 4, fontWeight: 700 }}>$!</span>}
                                        {financialUnknown && <span style={{ marginLeft: 4, color: '#888' }} title="Financial status unknown">?</span>}
                                      </label>
                                    );
                                  })}
                                </div>
                                )}
                              </div>
                            );
                          })()}

                          {participants.length > 0 && (
                            <button
                              className="btn-view-participants"
                              onClick={() => handleViewParticipants(program)}
                            >
                              Select Other Participants
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ADD/EDIT PROGRAM MODAL */}
      {showProgramModal && (
        <div className="coach-modal-overlay" onClick={() => setShowProgramModal(false)}>
          <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowProgramModal(false)}>&times;</button>
            <h3>{editingProgram ? 'Edit Session' : 'Add Session'}</h3>
            <form onSubmit={handleSaveProgram} className="admin-form">
              {!editingProgram && (
                <div className="form-group">
                  <label>How are the players added?</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { manual: false, title: 'Linked to Basketball Connect', note: "Players come in from the program's Comp Mapping." },
                      { manual: true, title: 'Manual (Unlinked)', note: "Nobody is added automatically. You add the players yourself." }
                    ].map(opt => {
                      const active = !!programForm.isManual === opt.manual;
                      return (
                        <label key={opt.title} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', cursor: 'pointer',
                          border: active ? '2px solid #1a73e8' : '1px solid #ccc', borderRadius: 6,
                          background: active ? 'rgba(26,115,232,0.08)' : 'transparent'
                        }}>
                          <input type="radio" name="sessionLinkMode" checked={active}
                            onChange={() => setProgramForm({ ...programForm, isManual: opt.manual })} style={{ marginTop: 3 }} />
                          <span><strong>{opt.title}</strong><br /><small>{opt.note}</small></span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Program Type:</label>
                <select value={programForm.programTypeId} onChange={(e) => setProgramForm({ ...programForm, programTypeId: e.target.value })} required>
                  <option value="">Select Program Type</option>
                  {programTypes.map(pt => (<option key={pt.id} value={pt.id}>{pt.name}</option>))}
                </select>
              </div>
              {!editingProgram && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!programForm.repeat}
                      onChange={(e) => setProgramForm({ ...programForm, repeat: e.target.checked, repeatFrom: programForm.repeatFrom || selectedDate })} />
                    Repeat weekly
                  </label>
                  {!programForm.isManual && programForm.programTypeId && regGroupsLoading && <small>Looking up the registration groups...</small>}
                  {!programForm.isManual && programForm.programTypeId && !regGroupsLoading && regGroups.length === 0 && (
                    <small style={{ display: 'block', marginTop: 4 }}>No Basketball Connect registration groups were found for this program, so the players come in from the program as a whole.</small>
                  )}
                </div>
              )}
              {groupMode ? (
                <div className="form-group" style={{ border: '1px solid #ddd', borderRadius: 6, padding: '10px' }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label>Start date:</label>
                      <input type="date" value={programForm.repeatFrom} onChange={(e) => setProgramForm({ ...programForm, repeatFrom: e.target.value })} required />
                    </div>
                    {programForm.repeat && (
                      <div style={{ flex: '1 1 140px' }}>
                        <label>End date:</label>
                        <input type="date" value={programForm.repeatUntil} min={programForm.repeatFrom || undefined} onChange={(e) => setProgramForm({ ...programForm, repeatUntil: e.target.value })} required />
                      </div>
                    )}
                  </div>
                  <small style={{ display: 'block', marginTop: 6 }}>
                    {programForm.repeat
                      ? 'Each ticked group gets a session every week between these dates, on its day and time.'
                      : 'Each ticked group gets one session, on its day in the week from the start date.'}
                  </small>
                  <label style={{ marginTop: 10, display: 'block' }}>Registration groups:</label>
                  {regGroups.map(g => {
                    const sl = programForm.groupSlots?.[g.divisionId] || { on: false, dow: '', start: '', end: '' };
                    const setSl = (patch) => setProgramForm({ ...programForm, groupSlots: { ...programForm.groupSlots, [g.divisionId]: { ...sl, ...patch } } });
                    return (
                      <div key={g.divisionId} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                          <input type="checkbox" checked={!!sl.on} onChange={(e) => setSl({ on: e.target.checked })} />
                          <strong>{g.divisionName}</strong>
                          <small>({g.registrants} registered{g.upcomingSessions ? `, ${g.upcomingSessions} upcoming session${g.upcomingSessions === 1 ? '' : 's'} already` : ''})</small>
                        </label>
                        {sl.on && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                            <select value={sl.dow} required onChange={(e) => setSl({ dow: e.target.value })}>
                              <option value="">Day</option>
                              {[[1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'], [5, 'Friday'], [6, 'Saturday'], [0, 'Sunday']].map(([d, n]) => (<option key={d} value={d}>{n}</option>))}
                            </select>
                            <input type="time" value={sl.start} required style={{ width: 120 }} onChange={(e) => setSl({ start: e.target.value })} />
                            <span>to</span>
                            <input type="time" value={sl.end} required style={{ width: 120 }} onChange={(e) => setSl({ end: e.target.value })} />
                          </div>
                        )}
                        {sl.on && (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <select value={sl.court || ''} onChange={(e) => setSl({ court: e.target.value })} aria-label={`Court for ${g.divisionName}`}>
                                <option value="">Court</option>
                                {coachingCourts.map(c => (<option key={c.id} value={c.name}>{c.name}</option>))}
                              </select>
                              <select
                                value=""
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v && !(sl.coachRows || []).some(r => r.opt.split('_')[0] === v.split('_')[0])) setSl({ coachRows: [...(sl.coachRows || []), { opt: v, codeId: '' }] });
                                }}
                                aria-label={`Add a coach to ${g.divisionName}`}
                              >
                                <option value="">+ Add coach</option>
                                {coaches.map(c => (<option key={`${c.id}_${c.coachQualificationId || 'x'}`} value={`${c.id}_${c.coachQualificationId || ''}`}>{c.name}{c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''}</option>))}
                              </select>
                            </div>
                            {(sl.coachRows || []).map((row) => {
                              const c = coaches.find(x => `${x.id}_${x.coachQualificationId || ''}` === row.opt);
                              const setRow = (patch) => setSl({ coachRows: sl.coachRows.map(r => (r.opt === row.opt ? { ...r, ...patch } : r)) });
                              return (
                                <div key={row.opt} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <span style={{ minWidth: 160, fontWeight: 600 }}>{c ? `${c.name}${c.qualificationLevel ? ` - ${c.qualificationLevel}` : ''}` : row.opt}</span>
                                  <select value={row.codeId || ''} onChange={(e) => setRow({ codeId: e.target.value })} aria-label={`Coaching code for ${c ? c.name : 'coach'}`}>
                                    <option value="">Coaching code</option>
                                    {coachCodes.map(cc => (<option key={cc.id} value={cc.id}>{cc.code}{cc.label ? ` - ${cc.label}` : ''}</option>))}
                                  </select>
                                  <button type="button" title="Remove coach"
                                    onClick={() => setSl({ coachRows: sl.coachRows.filter(r => r.opt !== row.opt) })}
                                    style={{ border: '1px solid #ccc', borderRadius: 14, padding: '2px 10px', background: '#f6f6f6', cursor: 'pointer' }}>
                                    &times;
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : !editingProgram && programForm.repeat ? (
                <div className="form-group" style={{ border: '1px solid #ddd', borderRadius: 6, padding: '10px' }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label>Start date:</label>
                      <input type="date" value={programForm.repeatFrom} onChange={(e) => setProgramForm({ ...programForm, repeatFrom: e.target.value })} required />
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <label>End date:</label>
                      <input type="date" value={programForm.repeatUntil} min={programForm.repeatFrom || undefined} onChange={(e) => setProgramForm({ ...programForm, repeatUntil: e.target.value })} required />
                    </div>
                  </div>
                  <label style={{ marginTop: 10, display: 'block' }}>Days and start times:</label>
                  {[[1, 'Monday'], [2, 'Tuesday'], [3, 'Wednesday'], [4, 'Thursday'], [5, 'Friday'], [6, 'Saturday'], [0, 'Sunday']].map(([dow, name]) => {
                    const on = programForm.repeatSlots && programForm.repeatSlots[dow] !== undefined;
                    const time = on ? programForm.repeatSlots[dow] : '';
                    const dur = parseInt(programForm.durationMinutes, 10);
                    let endTxt = '';
                    if (on && time && dur) {
                      const [h, m] = time.split(':').map(Number);
                      const tot = h * 60 + m + dur;
                      endTxt = `ends ${String(Math.floor(tot / 60) % 24).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
                    }
                    return (
                      <div key={dow} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, width: 120, cursor: 'pointer', margin: 0 }}>
                          <input type="checkbox" checked={on} onChange={(e) => {
                            const next = { ...(programForm.repeatSlots || {}) };
                            if (e.target.checked) next[dow] = programForm.startTime || ''; else delete next[dow];
                            setProgramForm({ ...programForm, repeatSlots: next });
                          }} />
                          {name}
                        </label>
                        {on && (
                          <>
                            <input type="time" value={time} required style={{ width: 130 }}
                              onChange={(e) => setProgramForm({ ...programForm, repeatSlots: { ...programForm.repeatSlots, [dow]: e.target.value } })} />
                            <small>{endTxt}</small>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="form-group">
                  <label>Start Time:</label>
                  <input type="time" value={programForm.startTime} onChange={(e) => setProgramForm({ ...programForm, startTime: e.target.value })} required />
                </div>
              )}
              {!groupMode && (
              <div className="form-group">
                <label>Duration (minutes):</label>
                <input type="number" min="15" step="15" value={programForm.durationMinutes} onChange={(e) => setProgramForm({ ...programForm, durationMinutes: e.target.value })} required />
              </div>
              )}
              {!groupMode && !(programForm.repeat && !editingProgram) && (
              <div className="form-group">
                <label>End Time:</label>
                <input
                  type="time"
                  readOnly
                  tabIndex={-1}
                  value={(() => {
                    const start = programForm.startTime;
                    const dur = parseInt(programForm.durationMinutes, 10);
                    if (!start || !dur) return '';
                    const [h, m] = start.split(':').map(Number);
                    if (Number.isNaN(h) || Number.isNaN(m)) return '';
                    const total = h * 60 + m + dur;
                    const eh = Math.floor(total / 60) % 24;
                    const em = total % 60;
                    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
                  })()}
                  style={{ background: '#f0f0f0', cursor: 'not-allowed' }}
                  title="Calculated from Start Time + Duration"
                />
              </div>
              )}
              {!groupMode && (
              <div className="form-group">
                <label>Court:</label>
                <select value={programForm.location} onChange={(e) => setProgramForm({ ...programForm, location: e.target.value })}>
                  <option value="">Select Court</option>
                  {coachingCourts.map(c => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </select>
              </div>
              )}
              <div className="form-group">
                <label>Notes:</label>
                <textarea
                  value={programForm.notes}
                  onChange={(e) => setProgramForm({ ...programForm, notes: e.target.value })}
                  placeholder="Add notes for this session..."
                  rows={4}
                  style={{ width: '100%', resize: 'vertical', minHeight: '80px', maxHeight: '300px', fontFamily: 'inherit', fontSize: '0.9rem', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="submit" className="btn-submit">{editingProgram ? 'Update' : (programForm.repeat || groupMode) ? 'Add Sessions' : 'Add Session'}</button>
                <button type="button" className="btn-cancel" onClick={() => setShowProgramModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PARTICIPANT SELECTION MODAL */}
      {showParticipantModal && (
        <div className="coach-modal-overlay" onClick={() => setShowParticipantModal(false)}>
          <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowParticipantModal(false)}>&times;</button>
            <h3>Select Other Participants — {participantModalData.programName}</h3>
            {(() => {
              // "Select Other Participants" = everyone enrolled in this
              // Coaching Program who is NOT in this session's Registration
              // Group. Includes already-added off-roster extras (selected=true
              // with inRegistrationGroup=false) so the coach can untick to
              // remove them.
              // Off-roster pool, sorted alphabetically by firstName then
              // lastName so coaches who know players by their first name
              // can scan the list quickly (per operator request 16 May
              // 2026 — surnames-first sort meant siblings clumped which
              // wasn't useful when picking by name).
              const list = participantModalData.participants
                .filter(p => p.inRegistrationGroup === false)
                .slice()
                .sort((a, b) => {
                  const fa = (a.firstName || '').toLowerCase();
                  const fb = (b.firstName || '').toLowerCase();
                  if (fa !== fb) return fa < fb ? -1 : 1;
                  const la = (a.lastName || '').toLowerCase();
                  const lb = (b.lastName || '').toLowerCase();
                  return la < lb ? -1 : la > lb ? 1 : 0;
                });
              if (participantModalData.participants.length === 0) {
                return <p style={{ color: '#666' }}>No participants enrolled in this program type. Add participants in Admin &gt; Participants and assign them to this program type.</p>;
              }
              if (list.length === 0) {
                return (
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>
                    Everyone enrolled in this Coaching Program is already in this session's Registration Group.
                    There are no off-roster participants to add.
                  </p>
                );
              }
              return (
                <div className="participant-checklist">
                  <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    These participants are enrolled in this Coaching Program but in a <em>different</em> Registration Group.
                    Tick to add them as an off-roster extra to this session. Untick to remove (only allowed if not yet attended).
                  </p>
                  <div className="responsive-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>Select</th>
                          <th>Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map(p => (
                          <tr key={p.id} style={{ backgroundColor: p.selected ? '#e8f5e9' : 'transparent' }}>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={!!p.selected}
                                onChange={() => handleToggleParticipant(participantModalData.programId, p.id)}
                                disabled={readOnly || (!!p.selected && !!p.attended)}
                                style={{ width: '18px', height: '18px', cursor: readOnly ? 'not-allowed' : 'pointer', accentColor: '#2e7d32' }}
                              />
                            </td>
                            <td style={{ fontWeight: p.selected ? 600 : 400 }}>{p.firstName} {p.lastName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#555' }}>
                    {list.filter(p => p.selected).length} off-roster participant(s) added to this session.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* COPY DAY MODAL */}
      {copySessionProgram && (
        <div className="coach-modal-overlay" onClick={closeCopySession}>
          <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeCopySession}>&times;</button>
            <h3>Copy session</h3>
            <p style={{ color: '#666', marginBottom: '0.75rem' }}>
              Copying <strong>{copySessionProgram.programTypeName}</strong>
              {' '}({(new Date(copySessionProgram.startDateTime)).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })}
              {' · '}{copySessionProgram.durationMinutes} min){' '}from <strong>{dowLong(selectedDate) || selectedDate}</strong>.
              <br />
              Same time, location, owner, notes and coach roster will be recreated on each chosen date. Participants are auto-populated for the new date.
              A target date that already has this session at the same time is skipped.
            </p>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Copy to date(s):</label>
              {copySessionDates.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <input
                    type="date"
                    value={d}
                    min={minDateStr}
                    max={maxDateStr}
                    onChange={(e) => setCopySessionDate(i, e.target.value)}
                    style={{ padding: '0.4rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.95rem' }}
                  />
                  <span style={{ fontWeight: 600, color: '#1565c0', fontSize: '0.85rem', minWidth: 90 }}>{dowLong(d)}</span>
                  {copySessionDates.length > 1 && (
                    <button onClick={() => removeCopySessionDate(i)} title="Remove this date" style={{ border: 'none', background: 'none', color: '#c62828', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', padding: '0 4px' }}>×</button>
                  )}
                </div>
              ))}
              <button
                onClick={addCopySessionDate}
                style={{ marginTop: 4, padding: '0.3rem 0.6rem', background: '#1565c0', color: 'white', border: 'none', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
              >
                + Add another date
              </button>
            </div>
            {copySessionResult && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: 4, fontSize: '0.85rem', color: '#1b5e20' }}>
                Created {copySessionResult.created.length} session(s){copySessionResult.created.length ? ` on ${copySessionResult.created.map(c => c.date).join(', ')}` : ''}.
                {copySessionResult.skipped.length > 0 && (
                  <div style={{ marginTop: 4, color: '#c62828' }}>
                    Skipped: {copySessionResult.skipped.map(s => `${s.date} (${s.reason})`).join('; ')}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleCopySession}
                disabled={copySessionLoading || copySessionDates.every(d => !d.trim())}
                style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}
              >
                {copySessionLoading ? 'Copying…' : 'Copy session'}
              </button>
              <button
                onClick={closeCopySession}
                style={{ padding: '0.5rem 1rem', background: '#999', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyModal && (
        <div className="coach-modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCopyModal(false)}>&times;</button>
            <h3>Copy Day</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Copy all programs from <strong>{dowLong(selectedDate) || selectedDate}</strong> to another date. This will replace any existing programs on the target date.
            </p>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>Target Date:</label>
              <input
                type="date"
                value={copyTargetDate}
                onChange={(e) => setCopyTargetDate(e.target.value)}
                min={minDateStr}
                max={maxDateStr}
                style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem' }}
              />
              <span style={{ marginLeft: 8, fontWeight: 600, color: '#1565c0' }}>{dowLong(copyTargetDate)}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn-submit"
                onClick={handleCopyDay}
                disabled={copyLoading || !copyTargetDate || copyTargetDate === selectedDate}
                style={{ padding: '0.5rem 1rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' }}
              >
                {copyLoading ? 'Copying...' : 'Copy'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setShowCopyModal(false)}
                style={{ padding: '0.5rem 1rem', background: '#999', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
